// frontend/src/dashboard/DashboardShell.tsx
//
// Worqly dashboard shell — premium-dashboards aesthetic, light + dark.
//
// Layout
//   ┌──────────┬─────────────────────────────────────────────────┐
//   │ Sidebar  │ Header (search, theme, notifs, user)            │
//   │ (collap. │─────────────────────────────────────────────────│
//   │ + drawer │ <TrialBanner />                                 │
//   │ on mobile│─────────────────────────────────────────────────│
//   │          │ <Outlet />  (routed page)                        │
//   └──────────┴─────────────────────────────────────────────────┘
//
// Notes
//   • Every backgrounded surface has a `dark:` companion so the layout
//     reads correctly in both themes — this was the main bug class on
//     the previous shell.
//   • Sidebar collapses to a 72px rail on desktop; drawer on mobile.
//   • Theme cycling lives in the sidebar footer as a 3-segment pill
//     (light / dark / system) so the user can pick directly instead
//     of cycling.
//   • Nav is hardcoded to match App.tsx routes. When the per-tenant
//     enabled-modules endpoint exists we'll filter from this same list.

import * as React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Home, FileText, Users, Package, Mail, CreditCard,
  Calendar, CalendarOff, Receipt, Banknote, Camera,
  Briefcase, Wallet, Settings as SettingsIcon, PenTool,
  Search, Menu, ChevronsLeft, ChevronsRight,
  Sun, Moon, Monitor, LogOut, Tag, FolderKanban, ListTodo,
  SlidersHorizontal, LifeBuoy, ShieldCheck, BarChart3, Bell, Network, CalendarClock, Target,
  ClipboardCheck,
} from 'lucide-react';
import { useAuth } from '../core/auth/AuthProvider';
import { usePermissions } from '../core/auth/usePermission';
import { useBranding } from '../core/branding/BrandingProvider';
import { useEnabledModules } from '../core/modules/EnabledModulesProvider';
import { useTheme, type ThemeMode } from '../core/theme/ThemeProvider';
import { PAGE_TRANSITION_V2 } from '../design-system/motion';
import { WorqlyMark } from '../design-system/components/WorqlyMark';
import { TrialBanner } from './TrialBanner';
import { ImpersonationBanner } from '../core/auth/ImpersonationBanner';
import { NotificationCenter } from '../modules/notifications/components/NotificationCenter';
import { CommandPalette } from '../modules/search/CommandPalette';
import { onboardingApi } from '../modules/onboarding/api';
import { useOnboarding } from '../modules/onboarding/OnboardingProvider';
import { useTour } from '../modules/onboarding/TourProvider';
import { STEP_PRESENTATION } from '../modules/onboarding/registry';
import { cn } from '../lib/utils';

/* ─────────────────────────── nav structure ─────────────────────────── */

type NavLeaf = { to: string; icon: React.ReactNode; label: string; end?: boolean; module?: string; permission?: string };
type NavBranch = { label: string; icon: React.ReactNode; children: NavLeaf[]; module?: string; permission?: string };
type NavEntry = NavLeaf | NavBranch;
type NavGroup = { label: string; items: NavEntry[] };

const isBranch = (n: NavEntry): n is NavBranch => 'children' in n;

/**
 * Filter NAV_GROUPS down to what a user may see, given the tenant's enabled
 * modules and the user's permission set.
 *
 * - A `module` key hides the entry entirely until the enabled-modules fetch
 *   resolves and the module isn't enabled for the tenant. Entries without a
 *   `module` key are always shown (module-wise). Before `loaded`, module
 *   gating is skipped (avoids a flash of a stripped-down nav).
 * - A `permission` key hides the entry unless the user holds it (or the
 *   owner wildcard `'*'`). Entries without a `permission` key are always
 *   shown (permission-wise).
 * - Branch entries (HR, Payroll, Projects, Monitoring, Expenses) carry a
 *   *default* `module`/`permission` that their children inherit when they
 *   don't specify their own — e.g. HR's Attendance/Leave leaves override
 *   the branch's `hr.employees.view` default with their own
 *   `hr.attendance.view_self` / `hr.leave.view_self` so a regular employee
 *   (who lacks `hr.employees.view`) still sees their own attendance/leave.
 *   A branch is shown only if its module is enabled AND at least one child
 *   survives the per-child filter.
 */
export function buildNav(perms: Set<string>, modules: Set<string>, loaded: boolean): NavGroup[] {
  const keep = (m?: string) => !m || !loaded || modules.has(m);
  const hasPerm = (p?: string) => !p || perms.has('*') || perms.has(p);
  const keepLeaf = (e: NavLeaf, parentModule?: string, parentPermission?: string) =>
    keep(e.module ?? parentModule) && hasPerm(e.permission ?? parentPermission);

  return NAV_GROUPS
    .map((g) => ({
      ...g,
      items: g.items
        .map((e): NavEntry =>
          isBranch(e) ? { ...e, children: e.children.filter((c) => keepLeaf(c, e.module, e.permission)) } : e
        )
        .filter((e) => (isBranch(e) ? keep(e.module) && e.children.length > 0 : keepLeaf(e))),
    }))
    .filter((g) => g.items.length > 0);
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', icon: <Home className="h-4 w-4" />, label: 'Home', end: true },
      // Cross-module (workforce + financial reports) — gated on reports.view
      // only, never on a single `module` key (mirrors billing/notifications).
      { to: '/dashboard/reports', icon: <BarChart3 className="h-4 w-4" />, label: 'Reports', permission: 'reports.view' },
    ],
  },
  {
    label: 'Money',
    items: [
      { to: '/dashboard/invoices', icon: <FileText className="h-4 w-4" />,   label: 'Invoices',      module: 'invoicing', permission: 'invoices.view' },
      { to: '/dashboard/esign',    icon: <PenTool className="h-4 w-4" />,    label: 'E-Sign',        module: 'esign',     permission: 'esign.view' },
      { to: '/dashboard/clients',  icon: <Users className="h-4 w-4" />,      label: 'Clients',       module: 'invoicing', permission: 'clients.view' },
      { to: '/dashboard/services', icon: <Package className="h-4 w-4" />,    label: 'Services',      module: 'invoicing', permission: 'services.view' },
      { to: '/dashboard/payments', icon: <CreditCard className="h-4 w-4" />, label: 'Payments',      module: 'payments', permission: 'payments.manage' },
      {
        label: 'Expenses',
        icon: <Receipt className="h-4 w-4" />,
        module: 'expenses',
        permission: 'expenses.read',
        children: [
          { to: '/dashboard/expenses',                 icon: <Receipt className="h-3.5 w-3.5" />,          label: 'All expenses' },
          // Permission keys match the backend route gates in api/modules/expenses/*.routes.ts.
          { to: '/dashboard/expenses/approvals',       icon: <ShieldCheck className="h-3.5 w-3.5" />,      label: 'Approvals',       permission: 'expenses.approve' },
          { to: '/dashboard/expenses/reimburse',       icon: <Banknote className="h-3.5 w-3.5" />,         label: 'Reimburse',       permission: 'expenses.reimburse' },
          { to: '/dashboard/expenses/categories',      icon: <Tag className="h-3.5 w-3.5" />,              label: 'Categories',      permission: 'expenses.manage_categories' },
          { to: '/dashboard/expenses/approval-chains', icon: <SlidersHorizontal className="h-3.5 w-3.5" />, label: 'Approval chains', permission: 'expenses.manage_categories' },
          // Routing policies decide WHICH chain a claim goes down. The backend
          // gates even the reads on expenses.approve — mirror that, not
          // manage_categories.
          { to: '/dashboard/expenses/approval-policies', icon: <SlidersHorizontal className="h-3.5 w-3.5" />, label: 'Approval policies', permission: 'expenses.approve' },
        ],
      },
      { to: '/dashboard/billing', icon: <Wallet className="h-4 w-4" />, label: 'Subscription', permission: 'billing.view' },
    ],
  },
  {
    label: 'Team',
    items: [
      {
        label: 'HR',
        icon: <Users className="h-4 w-4" />,
        module: 'hr',
        permission: 'hr.employees.view',
        children: [
          { to: '/dashboard/hr/team',          icon: <Users className="h-3.5 w-3.5" />,        label: 'People',       permission: 'hr.employees.view' },
          { to: '/dashboard/hr/org-chart',     icon: <Network className="h-3.5 w-3.5" />,      label: 'Org chart',    permission: 'hr.employees.view' },
          { to: '/dashboard/hr/attendance',    icon: <Calendar className="h-3.5 w-3.5" />,     label: 'Attendance',   permission: 'hr.attendance.view_self' },
          { to: '/dashboard/hr/leave',         icon: <CalendarOff className="h-3.5 w-3.5" />,  label: 'Leave',        permission: 'hr.leave.view_self' },
          { to: '/dashboard/hr/penalty-rules', icon: <Receipt className="h-3.5 w-3.5" />,      label: 'Penalty rules', permission: 'hr.penalty_rules.view' },
          { to: '/dashboard/hr/schedule',      icon: <CalendarClock className="h-3.5 w-3.5" />, label: 'Schedule',     permission: 'hr.schedule.view_all' },
          { to: '/dashboard/hr/my-schedule',   icon: <CalendarClock className="h-3.5 w-3.5" />, label: 'My schedule',  permission: 'hr.attendance.view_self' },
          { to: '/dashboard/hr/performance',    icon: <Target className="h-3.5 w-3.5" />,       label: 'Performance',    permission: 'hr.performance.view_all' },
          { to: '/dashboard/hr/my-performance', icon: <Target className="h-3.5 w-3.5" />,       label: 'My performance', permission: 'hr.attendance.view_self' },
        ],
      },
      // Roles & access lives at Settings → Access, but the people managing a
      // team look for it HERE after inviting someone. No `module` key — access
      // management exists even for HR-disabled tenants.
      { to: '/dashboard/settings/access', icon: <ShieldCheck className="h-4 w-4" />, label: 'Roles & access', permission: 'access.manage' },
      {
        label: 'Payroll',
        icon: <Banknote className="h-4 w-4" />,
        module: 'payroll',
        permission: 'payroll.view',
        children: [
          { to: '/dashboard/payroll',          icon: <Banknote className="h-3.5 w-3.5" />, label: 'Runs',     end: true },
          { to: '/dashboard/payroll/payslips', icon: <Receipt className="h-3.5 w-3.5" />,  label: 'Payslips', permission: 'payroll.view_self' },
          { to: '/dashboard/payroll/reports',  icon: <Wallet className="h-3.5 w-3.5" />,   label: 'Reports' },
        ],
      },
    ],
  },
  {
    label: 'Work',
    items: [
      {
        label: 'Projects',
        icon: <FolderKanban className="h-4 w-4" />,
        module: 'projects',
        permission: 'projects.view',
        children: [
          { to: '/dashboard/projects',          icon: <FolderKanban className="h-3.5 w-3.5" />, label: 'Projects', end: true },
          { to: '/dashboard/projects/my-tasks', icon: <ListTodo className="h-3.5 w-3.5" />,      label: 'My Tasks', permission: 'projects.task_self' },
          // Must carry its own permission: without one it inherits the branch's
          // `projects.view` (seeded to every employee) while the route 403s on
          // `projects.reports` — employees saw a nav item leading to no-access.
          { to: '/dashboard/projects/reports',  icon: <BarChart3 className="h-3.5 w-3.5" />,     label: 'Reports', permission: 'projects.reports' },
          // Same key the approve/reject/reopen routes enforce — an employee
          // without it never sees a queue they cannot act on.
          { to: '/dashboard/projects/timesheets', icon: <ClipboardCheck className="h-3.5 w-3.5" />, label: 'Timesheets', permission: 'time_entries.approve' },
        ],
      },
      {
        label: 'Monitoring',
        icon: <Camera className="h-4 w-4" />,
        module: 'monitoring',
        permission: 'monitoring.view_all',
        children: [
          { to: '/dashboard/monitoring',           icon: <Camera className="h-3.5 w-3.5" />,       label: 'Screenshots', end: true },
          { to: '/dashboard/monitoring/analytics', icon: <BarChart3 className="h-3.5 w-3.5" />,    label: 'Analytics' },
          { to: '/dashboard/monitoring/settings',  icon: <SettingsIcon className="h-3.5 w-3.5" />, label: 'Settings' },
        ],
      },
    ],
  },
  {
    label: 'Comms',
    items: [
      { to: '/dashboard/email',         icon: <Mail className="h-4 w-4" />,    label: 'Email',         module: 'email', permission: 'email.view' },
      { to: '/dashboard/notifications', icon: <Bell className="h-4 w-4" />,    label: 'Notifications' },
      { to: '/dashboard/support',       icon: <LifeBuoy className="h-4 w-4" />, label: 'Support' },
    ],
  },
];

const INITIAL_OF = (s: string) => (s ? s.charAt(0).toUpperCase() : '?');

/* ──────────────────────────────── shell ─────────────────────────────── */

export function DashboardShell() {
  const { identity, logout } = useAuth();
  const { platform, tenant } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh: refreshOnboarding } = useOnboarding();
  const tour = useTour();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('worqly_nav_collapsed') === '1';
  });
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);

  // Close the mobile drawer on route change.
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // ⌘K / Ctrl+K opens the global command palette.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Persist collapse preference.
  React.useEffect(() => {
    try { window.localStorage.setItem('worqly_nav_collapsed', collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  const { modules, loaded } = useEnabledModules();
  const perms = usePermissions();

  // Hide module-bound nav entries the tenant hasn't enabled. Entries without a
  // `module` key are always shown. Before the fetch resolves, show everything
  // (avoids a flash of a stripped-down nav).
  const keep = React.useCallback(
    (m?: string) => !m || !loaded || modules.has(m),
    [modules, loaded],
  );

  // Hide permission-bound nav entries the user can't use (they'd 403 on
  // click). Entries without a `permission` key are always shown; '*' is the
  // owner wildcard from /me.
  const hasPerm = React.useCallback(
    (p?: string) => !p || perms.has('*') || perms.has(p),
    [perms],
  );

  const visibleGroups = React.useMemo(
    () => buildNav(perms, modules, loaded),
    [perms, modules, loaded],
  );

  // Click outside closes the user menu.
  const userMenuRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!userMenuRef.current?.contains(e.target as Node)) setUserMenuOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);

  const companyName = tenant?.company_name ?? 'Your Workspace';
  const userEmail = identity?.type === 'user' ? identity.email : '';
  const userName  = identity?.type === 'user' ? identity.name : '';
  const firstName = userName?.split(' ')[0] || userEmail.split('@')[0] || 'You';

  return (
    <div className="relative min-h-screen flex text-ink dark:text-white">
      {/* Fixed, top-of-viewport — visible on every screen while impersonating,
          renders nothing otherwise. */}
      <ImpersonationBanner />

      {/* Atmospheric mesh — full-bleed canvas tint behind everything. */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-pds-mesh opacity-90" />

      {/* Mobile drawer scrim */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.button
            type="button"
            aria-label="Close menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-ink/40 dark:bg-black/60 backdrop-blur-sm lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* ─────────────────────── Sidebar ─────────────────────── */}
      <aside
        role="navigation"
        aria-label="Main navigation"
        className={cn(
          'fixed lg:sticky top-0 z-50 h-screen shrink-0 flex flex-col',
          'transition-[width,transform] duration-300 ease-out',
          // Glass — works in both themes
          'bg-white/65 dark:bg-white/[0.04] backdrop-blur-2xl',
          'ring-1 ring-ink/[0.08] dark:ring-white/10',
          'shadow-[10px_0_30px_-20px_rgba(20,12,30,0.18)] dark:shadow-[10px_0_30px_-20px_rgba(0,0,0,0.6)]',
          // Width: full on mobile, rail vs full on desktop
          'w-72',
          collapsed ? 'lg:w-[76px]' : 'lg:w-72',
          // Slide in/out for mobile
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Brand row — collapsed rail (76px) stacks mark above the expand
            button; the old absolute right-2 button overlapped the centered dot. */}
        <div className={cn(
          'px-4 pt-5 pb-4 flex items-center justify-between gap-2',
          collapsed && 'lg:flex-col lg:justify-start lg:gap-3',
        )}>
          <div className={cn('flex items-center min-w-0', collapsed ? 'lg:justify-center lg:w-full' : '')}>
            <WorqlyMark size="md" markOnly={collapsed} />
          </div>
          {/* Mobile close */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="lg:hidden inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-ink shadow-pill dark:bg-white/10 dark:text-white"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
          {/* Desktop collapse */}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            className={cn(
              'hidden lg:inline-flex h-7 w-7 items-center justify-center rounded-full shrink-0',
              'bg-white/80 text-ink shadow-pill hover:bg-white',
              'dark:bg-white/[0.08] dark:text-white/80 dark:hover:bg-white/[0.14]',
              'transition-colors',
            )}
          >
            {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <ChevronsLeft className="h-3.5 w-3.5" />}
          </button>
        </div>

        {/* Tenant card */}
        {!collapsed && (
          <div className="mx-3 mb-4">
            <div className="flex items-center gap-3 p-2.5 rounded-2xl bg-white/75 dark:bg-white/[0.06] backdrop-blur-md ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-pill">
              {tenant?.company_logo_url ? (
                // object-contain, not object-cover: most logos are wider than
                // tall, and cover CROPS them inside the square slot.
                <img
                  src={tenant.company_logo_url}
                  alt=""
                  className="h-9 w-9 rounded-xl object-contain bg-white p-0.5 ring-1 ring-ink/10 dark:ring-white/10 shrink-0"
                />
              ) : (
                <div className="h-9 w-9 rounded-xl bg-ink-900 text-white flex items-center justify-center text-[14px] font-bold shrink-0">
                  {INITIAL_OF(companyName)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold tracking-[-0.005em] text-ink dark:text-white truncate">
                  {companyName}
                </p>
                <p className="text-[11px] text-ink/55 dark:text-white/55 truncate">
                  on {platform?.name ?? 'Worqly'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed-state tenant glyph */}
        {collapsed && (
          <div className="mx-auto mb-4">
            {tenant?.company_logo_url ? (
              <img src={tenant.company_logo_url} alt="" className="h-9 w-9 rounded-xl object-contain bg-white p-0.5 ring-1 ring-ink/10 dark:ring-white/10" />
            ) : (
              <div className="h-9 w-9 rounded-xl bg-ink-900 text-white flex items-center justify-center text-[14px] font-bold">
                {INITIAL_OF(companyName)}
              </div>
            )}
          </div>
        )}

        {/* Scrolling nav */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 pb-4 space-y-5">
          {visibleGroups.map((g) => (
            <div key={g.label}>
              {!collapsed && (
                <p className="px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-ink/45 dark:text-white/40 mb-2">
                  {g.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {g.items.map((entry) =>
                  isBranch(entry) ? (
                    <NavBranchItem key={entry.label} branch={entry} collapsed={collapsed} />
                  ) : (
                    <NavItem key={entry.to} item={entry} collapsed={collapsed} />
                  )
                )}
              </ul>
            </div>
          ))}
          <div className="pt-3 mt-1 border-t border-ink/8 dark:border-white/10">
            <ul className="space-y-0.5">
              <NavBranchItem
                branch={{
                  label: 'Settings',
                  icon: <SettingsIcon className="h-4 w-4" />,
                  children: [
                    { to: '/dashboard/settings',           icon: <SettingsIcon className="h-3.5 w-3.5" />,      label: 'Workspace', end: true },
                    { to: '/dashboard/settings/access',    icon: <ShieldCheck className="h-3.5 w-3.5" />,       label: 'Access', permission: 'access.manage' },
                    { to: '/dashboard/settings/invoicing', icon: <SlidersHorizontal className="h-3.5 w-3.5" />, label: 'Invoicing settings', module: 'invoicing', permission: 'invoices.write' },
                  ].filter((c) => keep(c.module) && hasPerm(c.permission)),
                }}
                collapsed={collapsed}
              />
            </ul>
          </div>
        </nav>

        {/* Footer — theme + user */}
        <div className="border-t border-ink/8 dark:border-white/10 p-3 space-y-3">
          {!collapsed && <ThemeSegmentToggle />}
          {collapsed && <CollapsedThemeButton />}

          <div className={cn('flex items-center gap-3 px-1', collapsed && 'lg:justify-center lg:px-0')}>
            <div className="h-9 w-9 rounded-full bg-coral text-white flex items-center justify-center text-[13px] font-bold shadow-pill shrink-0">
              {INITIAL_OF(userName) || INITIAL_OF(userEmail)}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-ink dark:text-white truncate">
                    {userName || userEmail}
                  </p>
                  {userName && userEmail ? (
                    <p className="text-[11px] text-ink/55 dark:text-white/55 truncate">{userEmail}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => logout()}
                  aria-label="Sign out"
                  className="text-ink/55 hover:text-coral dark:text-white/55 dark:hover:text-coral transition-colors p-1 -mr-1"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ─────────────────────── Main column ─────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30">
          <div
            aria-hidden
            className="absolute inset-0 -z-10 bg-white/55 dark:bg-[#0E0E14]/65 backdrop-blur-xl border-b border-white/40 dark:border-white/10"
          />
          <div className="h-16 px-4 sm:px-6 lg:px-10 flex items-center gap-3">
            {/* Mobile menu */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-pill text-ink dark:bg-white/10 dark:text-white"
            >
              <Menu className="h-4 w-4" />
            </button>

            {/* Company name (mobile only — sidebar shows it on desktop) */}
            <p className="lg:hidden text-[13px] font-semibold text-ink dark:text-white truncate max-w-[40%]">
              {companyName}
            </p>

            {/* Search pill — opens the global command palette */}
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Open search (Command or Control K)"
              className={cn(
                'hidden md:flex flex-1 max-w-xl items-center gap-2.5 h-10 px-4 rounded-full cursor-pointer text-left',
                'bg-white/72 dark:bg-white/[0.06] backdrop-blur-md shadow-pill text-[13px]',
                'text-ink dark:text-white/80',
                'ring-1 ring-ink/[0.08] dark:ring-white/10',
              )}
            >
              <Search className="h-3.5 w-3.5 text-ink/45 dark:text-white/40" strokeWidth={2} />
              <span className="text-ink/50 dark:text-white/45 select-none">
                Jump to invoice, client, person…
              </span>
              <kbd
                className={cn(
                  'ml-auto inline-flex items-center gap-0.5 text-[10px] font-semibold tracking-wide',
                  'text-ink/55 dark:text-white/50 px-1.5 py-0.5 rounded-md',
                  'bg-ink/5 dark:bg-white/[0.08] ring-1 ring-ink/10 dark:ring-white/10',
                )}
              >
                ⌘K
              </kbd>
            </button>
            <div className="flex-1 md:hidden" />

            {/* Right cluster */}
            <NotificationCenter />

            {/* User chip — opens menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label="Open account menu"
                onClick={() => setUserMenuOpen((v) => !v)}
                className={cn(
                  'inline-flex h-9 items-center gap-2 px-2 sm:pl-2 sm:pr-3.5 rounded-full',
                  'bg-white/80 dark:bg-white/[0.08] backdrop-blur-md shadow-pill',
                  'hover:bg-white dark:hover:bg-white/[0.14] transition-colors',
                )}
              >
                <span className="h-6 w-6 rounded-full bg-coral text-white flex items-center justify-center text-[11px] font-bold">
                  {INITIAL_OF(userName) || INITIAL_OF(userEmail)}
                </span>
                <span className="hidden sm:inline text-[12.5px] font-semibold text-ink dark:text-white truncate max-w-[140px]">
                  {firstName}
                </span>
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    role="menu"
                    initial={{ opacity: 0, scale: 0.96, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: -4 }}
                    transition={{ duration: 0.14 }}
                    className={cn(
                      'absolute right-0 top-11 w-60 rounded-2xl p-2 z-50 origin-top-right',
                      'bg-white/95 dark:bg-[#15151B]/95 backdrop-blur-xl',
                      'ring-1 ring-ink/10 dark:ring-white/10',
                      'shadow-[0_18px_60px_-18px_rgba(15,15,18,0.35)]',
                    )}
                  >
                    <div className="px-3 pt-2 pb-3 border-b border-ink/8 dark:border-white/10">
                      <p className="text-[13px] font-semibold text-ink dark:text-white truncate">
                        {userName || firstName}
                      </p>
                      {userEmail && (
                        <p className="text-[11.5px] text-ink/55 dark:text-white/55 truncate mt-0.5">
                          {userEmail}
                        </p>
                      )}
                    </div>
                    <div className="pt-2">
                      <UserMenuLink to="/dashboard/settings" label="Workspace settings" />
                      {hasPerm('billing.view') && <UserMenuLink to="/dashboard/billing" label="Subscription" />}
                      <button
                        type="button"
                        role="menuitem"
                        onClick={async () => {
                          try {
                            await onboardingApi.resume();
                          } catch (err: any) {
                            toast.error('Could not resume the setup guide', { description: err?.message });
                          } finally {
                            // The guide state is now held by <OnboardingProvider>
                            // ABOVE this shell, so landing on /dashboard no
                            // longer remounts a component that refetches it.
                            // Without this the panel would stay hidden until a
                            // full page reload, even though the server flipped
                            // the status back to 'active'.
                            const next = await refreshOnboarding();
                            navigate('/dashboard');
                            // Explicitly asking for the guide should OPEN the
                            // walkthrough, not just un-hide the checklist —
                            // this is also the recovery path for users whose
                            // first-run auto-open never fired.
                            const step = next?.steps.find(
                              (s) => !s.done && (STEP_PRESENTATION[s.key]?.stops.length ?? 0) > 0,
                            );
                            if (step) tour.start(STEP_PRESENTATION[step.key].stops);
                          }
                        }}
                        className={cn(
                          'w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl',
                          'text-[13px] font-medium',
                          'text-ink/75 dark:text-white/75',
                          'hover:bg-ink/5 dark:hover:bg-white/[0.06] hover:text-ink dark:hover:text-white',
                          'transition-colors',
                        )}
                      >
                        Setup guide
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => logout()}
                        className={cn(
                          'w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl',
                          'text-[13px] font-medium',
                          'text-ink dark:text-white',
                          'hover:bg-coral hover:text-white dark:hover:bg-coral dark:hover:text-white',
                          'transition-colors',
                        )}
                      >
                        <LogOut className="h-3.5 w-3.5" />
                        Sign out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Trial banner slot — owned by a separate agent */}
        <div className="px-4 sm:px-6 lg:px-10 pt-4">
          <TrialBanner />
        </div>

        {/* Routed content */}
        <main className="flex-1 overflow-y-auto flex flex-col" role="main">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              variants={PAGE_TRANSITION_V2}
              initial="initial"
              animate="animate"
              exit="exit"
              className="flex-1"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>

          {/* Subtle "designed by" credit — sits at the bottom of the scroll
              container so it only appears below the page content. */}
          <footer className="mt-auto px-6 py-4 flex justify-end">
            <p className="text-[11px] text-ink/40 dark:text-white/35 tracking-[0.04em]">
              Designed &amp; developed by{' '}
              <a
                href="https://www.naqvix.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-ink/55 dark:text-white/55 hover:text-coral dark:hover:text-coral underline-offset-2 hover:underline transition-colors"
              >
                NaqviX
              </a>
            </p>
          </footer>
        </main>
      </div>

      {/* Global search command palette (⌘K) */}
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

/* ─────────────────────────── nav items ─────────────────────────── */

function NavItem({ item, collapsed }: { item: NavLeaf; collapsed: boolean }) {
  return (
    <li>
      <NavLink
        to={item.to}
        end={item.end}
        title={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center gap-3 rounded-2xl px-3 py-2.5',
            'text-[13px] tracking-[-0.005em] font-medium transition-colors',
            collapsed && 'lg:justify-center lg:px-2',
            isActive
              ? 'text-white font-semibold'
              : 'text-ink/70 dark:text-white/70 hover:text-ink dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/[0.06]',
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <motion.span
                layoutId="dashNavActive"
                className="absolute inset-0 -z-10 rounded-2xl bg-ink-900 dark:bg-coral shadow-[0_8px_22px_-10px_rgba(0,0,0,0.45)]"
                transition={{ type: 'spring', stiffness: 350, damping: 32 }}
              />
            )}
            <span className="flex h-5 w-5 items-center justify-center shrink-0">{item.icon}</span>
            {!collapsed && <span className="truncate">{item.label}</span>}
            {isActive && !collapsed && (
              <span aria-hidden className="ml-auto size-1.5 rounded-full bg-coral dark:bg-white" />
            )}
          </>
        )}
      </NavLink>
    </li>
  );
}

function NavBranchItem({ branch, collapsed }: { branch: NavBranch; collapsed: boolean }) {
  const location = useLocation();
  const isActiveChild = branch.children.some((c) =>
    location.pathname === c.to || location.pathname.startsWith(c.to + '/')
  );
  const [open, setOpen] = React.useState<boolean>(isActiveChild);

  // Re-open the branch if route lands inside it.
  React.useEffect(() => { if (isActiveChild) setOpen(true); }, [isActiveChild]);

  // In collapsed mode we render the branch children inline as bare icons.
  if (collapsed) {
    return (
      <>
        {branch.children.map((c) => (
          <NavItem key={c.to} item={c} collapsed />
        ))}
      </>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'group relative flex w-full items-center gap-3 rounded-2xl px-3 py-2.5',
          'text-[13px] tracking-[-0.005em] font-medium transition-colors text-left',
          isActiveChild
            ? 'text-ink dark:text-white font-semibold'
            : 'text-ink/70 dark:text-white/70 hover:text-ink dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/[0.06]',
        )}
      >
        <span className="flex h-5 w-5 items-center justify-center shrink-0">{branch.icon}</span>
        <span className="truncate flex-1">{branch.label}</span>
        <motion.span
          aria-hidden
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.16 }}
          className="text-ink/40 dark:text-white/40"
        >
          ›
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden pl-3 ml-3.5 border-l border-ink/10 dark:border-white/10 mt-0.5 space-y-0.5"
          >
            {branch.children.map((c) => (
              <li key={c.to}>
                <NavLink
                  to={c.to}
                  end={c.end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-xl px-2.5 py-2',
                      'text-[12.5px] font-medium transition-colors',
                      isActive
                        ? 'text-ink dark:text-white bg-white/65 dark:bg-white/[0.08] shadow-pill'
                        : 'text-ink/65 dark:text-white/65 hover:text-ink dark:hover:text-white hover:bg-white/40 dark:hover:bg-white/[0.05]',
                    )
                  }
                >
                  <span className="flex h-4 w-4 items-center justify-center shrink-0 opacity-70">
                    {c.icon}
                  </span>
                  <span className="truncate">{c.label}</span>
                </NavLink>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}

/* ─────────────────────────── theme controls ─────────────────────────── */

const THEME_MODES: ThemeMode[] = ['light', 'dark', 'system'];
const THEME_ICONS: Record<ThemeMode, React.ReactNode> = {
  light:  <Sun className="h-3.5 w-3.5" />,
  dark:   <Moon className="h-3.5 w-3.5" />,
  system: <Monitor className="h-3.5 w-3.5" />,
};
const THEME_LABEL: Record<ThemeMode, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};

function ThemeSegmentToggle() {
  const { mode, setMode } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        'flex items-center p-1 rounded-full',
        'bg-white/60 dark:bg-white/[0.06] backdrop-blur-md',
        'ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-pill',
      )}
    >
      {THEME_MODES.map((m) => {
        const active = mode === m;
        return (
          <button
            key={m}
            role="radio"
            aria-checked={active}
            aria-label={THEME_LABEL[m]}
            onClick={() => setMode(m)}
            className={cn(
              'flex-1 inline-flex items-center justify-center gap-1.5',
              'rounded-full px-2.5 py-1.5 text-[11.5px] font-semibold transition-colors',
              active
                ? 'bg-ink-900 text-white shadow-pill dark:bg-coral dark:text-white'
                : 'text-ink/60 dark:text-white/60 hover:text-ink dark:hover:text-white',
            )}
          >
            {THEME_ICONS[m]}
            <span className="hidden xl:inline">{THEME_LABEL[m]}</span>
          </button>
        );
      })}
    </div>
  );
}

function CollapsedThemeButton() {
  const { mode, setMode } = useTheme();
  // Cycle on click in collapsed view (limited horizontal space).
  const next: Record<ThemeMode, ThemeMode> = { light: 'dark', dark: 'system', system: 'light' };
  return (
    <button
      type="button"
      onClick={() => setMode(next[mode])}
      aria-label={`Theme: ${THEME_LABEL[mode]}. Click to cycle.`}
      className={cn(
        'lg:mx-auto inline-flex h-9 w-9 items-center justify-center rounded-full',
        'bg-white/80 dark:bg-white/[0.08] shadow-pill text-ink dark:text-white',
        'hover:bg-white dark:hover:bg-white/[0.14] transition-colors',
      )}
    >
      {THEME_ICONS[mode]}
    </button>
  );
}

/* ─────────────────────────── user menu ─────────────────────────── */

function UserMenuLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      role="menuitem"
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-medium transition-colors',
          isActive
            ? 'bg-ink/5 dark:bg-white/[0.06] text-ink dark:text-white'
            : 'text-ink/75 dark:text-white/75 hover:bg-ink/5 dark:hover:bg-white/[0.06] hover:text-ink dark:hover:text-white',
        )
      }
    >
      {label}
    </NavLink>
  );
}
